import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLocale, useT, EN_AVAILABLE_ROUTES } from "../lib/i18n";
import commonFr from "../locales/fr/common.json";
import commonEn from "../locales/en/common.json";

// Two symmetrical nav definitions. Slugs of proper nouns (novel titles) never change;
// only high-level route stems switch (/auteur ↔ /en/author).
const NAV_FR = [
  { key: "nav.home", to: "/" },
  { key: "nav.author", to: "/auteur" },
  { key: "nav.novels", to: "/romans" },
  { key: "nav.cinema", to: "/cinema/protocole-zero" },
  { key: "nav.news", to: "/actualites" },
  { key: "nav.climate", to: "/journal-climat" },
  { key: "nav.press", to: "/presse" },
  { key: "nav.publishers", to: "/editeurs-agents" },
  { key: "nav.contact", to: "/contact" },
];

// EN nav — pruned client-side by EN_AVAILABLE_ROUTES so we never expose links to
// routes that haven't yet been translated (rule "aucun lien cassé"). Each new LOT
// (3B/3C/3D) unlocks entries by adding their route to EN_AVAILABLE_ROUTES.
const NAV_EN_FULL = [
  { key: "nav.home", to: "/en" },
  { key: "nav.author", to: "/en/author" },
  { key: "nav.novels", to: "/en/novels" },
  { key: "nav.cinema", to: "/en/film/protocol-zero" },
  { key: "nav.news", to: "/en/news" },
  { key: "nav.climate", to: "/en/climate-journal" },
  { key: "nav.press", to: "/en/press" },
  { key: "nav.publishers", to: "/en/publishers-agents" },
  { key: "nav.contact", to: "/en/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const { locale, switchTo } = useLocale();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const t = useT({ fr: commonFr, en: commonEn });

  const links = locale === "en"
    ? NAV_EN_FULL.filter((l) => EN_AVAILABLE_ROUTES.has(l.to))
    : NAV_FR;
  // Espace pro CTA — only exposed on locales where its target route exists.
  // On EN, hide it entirely until LOT 3D delivers `/en/publishers-agents`. This
  // avoids the "silent EN → FR" flow explicitly forbidden by the audit rules.
  const proAvailable = locale === "en"
    ? EN_AVAILABLE_ROUTES.has("/en/publishers-agents")
    : true;
  const proHref = locale === "en" ? "/en/publishers-agents" : "/editeurs-agents";
  const homeHref = locale === "en" ? "/en" : "/";

  const isFrActive = locale === "fr";
  const isEnActive = locale === "en";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      data-locale={locale}
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
        scrolled ? "bg-[var(--ivory)]/95 backdrop-blur-sm border-b border-[var(--line)]" : ""
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
        <Link
          to={homeHref}
          data-testid="header-logo"
          className="flex flex-col leading-none"
          aria-label={locale === "en" ? "Bruno Courtin — home" : "Bruno Courtin — accueil"}
        >
          <span className="font-display text-2xl md:text-[26px] tracking-tight text-[var(--ink)]">Bruno Courtin</span>
          <span className="eyebrow mt-1">
            {locale === "en" ? "Author — Novels" : "Auteur — Romans"}
          </span>
        </Link>

        <nav
          className="hidden lg:flex items-center gap-8"
          aria-label={locale === "en" ? "Main navigation" : "Navigation principale"}
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key.replace(/\W+/g, "-")}`}
              className={({ isActive }) =>
                `nav-link text-[13px] font-body text-[var(--ink)] ${isActive ? "active" : ""}`
              }
              end={l.to === "/" || l.to === "/en"}
            >
              {t(l.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="hidden md:inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[var(--parchment)] border border-[var(--line-strong)] shadow-sm text-[11px] font-mono"
            role="group"
            aria-label={locale === "en" ? "Language switcher" : "Sélecteur de langue"}
          >
            <button
              data-testid="lang-switch-fr"
              onClick={() => switchTo("fr")}
              className={`px-2 py-1 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--parchment)] ${
                isFrActive
                  ? "text-[var(--ink)] font-semibold underline underline-offset-4"
                  : "text-[var(--brown)]/85 hover:text-[var(--ink)]"
              }`}
              aria-label={locale === "en" ? "Switch to French" : "Version française"}
              aria-current={isFrActive ? "true" : undefined}
              aria-pressed={isFrActive}
              title={locale === "en" ? "French version" : "Version française"}
              lang="fr"
            >{t("lang.fr")}</button>
            <span className="text-[var(--brown)]/50" aria-hidden="true">/</span>
            <button
              data-testid="lang-switch-en"
              onClick={() => switchTo("en")}
              className={`px-2 py-1 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--parchment)] ${
                isEnActive
                  ? "text-[var(--ink)] font-semibold underline underline-offset-4"
                  : "text-[var(--brown)]/85 hover:text-[var(--ink)]"
              }`}
              aria-label={locale === "en" ? "English version" : "Passer en anglais"}
              aria-current={isEnActive ? "true" : undefined}
              aria-pressed={isEnActive}
              title={locale === "en" ? "English version" : "English version"}
              lang="en"
            >{t("lang.en")}</button>
          </div>

          <Link
            to={proHref}
            data-testid="cta-espace-pro"
            className={`hidden md:inline-flex btn-editorial filled ${proAvailable ? "" : "!hidden"}`}
            aria-hidden={proAvailable ? undefined : true}
          >
            <span>{t("nav.pro_space")}</span>
          </Link>

          <button
            data-testid="menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-[var(--ink)]"
            aria-label={locale === "en" ? (open ? "Close menu" : "Open menu") : (open ? "Fermer le menu" : "Ouvrir le menu")}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            <span className="block w-6 h-[1px] bg-current mb-[6px]" />
            <span className="block w-6 h-[1px] bg-current mb-[6px]" />
            <span className="block w-4 h-[1px] bg-current ml-auto" />
          </button>
        </div>
      </div>

      {open && (
        <div
          id="mobile-menu"
          className="lg:hidden theme-ivory border-t border-[var(--line)] px-6 py-6 space-y-4"
          data-testid="mobile-menu"
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-mobile-${l.key.replace(/\W+/g, "-")}`}
              className="block font-display text-2xl text-[var(--ink)]"
              end={l.to === "/" || l.to === "/en"}
            >
              {t(l.key)}
            </NavLink>
          ))}
          <div
            className="inline-flex items-center gap-1 pt-4 mt-4 px-2 py-1 rounded-full bg-[var(--parchment)]/90 border border-[var(--line)] self-start"
            role="group"
            aria-label={locale === "en" ? "Language switcher" : "Sélecteur de langue"}
          >
            <button
              data-testid="lang-switch-fr-mobile"
              onClick={() => switchTo("fr")}
              className={`px-2 py-1 font-mono text-[11px] tracking-widest rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)] ${
                isFrActive
                  ? "text-[var(--ink)] font-semibold underline underline-offset-4"
                  : "text-[var(--brown)]/85 hover:text-[var(--ink)]"
              }`}
              aria-current={isFrActive ? "true" : undefined}
              aria-pressed={isFrActive}
              aria-label={locale === "en" ? "Switch to French" : "Version française"}
              lang="fr"
            >{t("lang.fr")}</button>
            <span className="text-[var(--brown)]/50" aria-hidden="true">/</span>
            <button
              data-testid="lang-switch-en-mobile"
              onClick={() => switchTo("en")}
              className={`px-2 py-1 font-mono text-[11px] tracking-widest rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)] ${
                isEnActive
                  ? "text-[var(--ink)] font-semibold underline underline-offset-4"
                  : "text-[var(--brown)]/85 hover:text-[var(--ink)]"
              }`}
              aria-current={isEnActive ? "true" : undefined}
              aria-pressed={isEnActive}
              aria-label={locale === "en" ? "English version" : "Passer en anglais"}
              lang="en"
            >{t("lang.en")}</button>
          </div>
          <Link to={proHref} className={`btn-editorial filled mt-4 ${proAvailable ? "" : "!hidden"}`} data-testid="cta-espace-pro-mobile" aria-hidden={proAvailable ? undefined : true}>
            {t("nav.pro_space")}
          </Link>
        </div>
      )}
    </header>
  );
}
